'use client';

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useListing } from '../../../../../Contexts/ListingProvider';
import adminModule from '../../../../../api/modules/admin.module';
import { formatNumber } from '../../../../../utils/common';
import { useListingSuggestedPrice } from '../../../../../hooks/useListingSuggestedPrice';
import { computeBuyerPrice, priceBreakdown } from '../../../../../utils/listingPricing';
import PricingQuantityStepView from './PricingQuantityStep.view';

/**
 * Step 3, price and quantity. The container.
 *
 * Every query key, every effect dependency list and every `updateFormData` call
 * below is unchanged. The comments that explain WHY a value is what it is are
 * kept verbatim, because they document a real backend contract, not a layout.
 */
const PricingQuantityStep = ({ formData, updateFormData, errors, titleRef }) => {
  const { lookUpData } = useListing();

  // Fetch system settings to get dynamic platform fee
  const { data: systemSettings } = useQuery({
    queryKey: ['systemSettings'],
    queryFn: async () => {
      const result = await adminModule.getSystemSettings();
      if (result.success) {
        return result.data;
      }
      return { sell_in_person: false, platform_fee: 5 };
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const platformFee = systemSettings?.platform_fee || 5;
  const feeRate = (platformFee || 0) / 100;

  // Fresh, finish-aware market price. CardDetailsStep warmed this query at
  // card-select time, so it normally resolves from cache; changing finish
  // or condition re-fetches (new query key). Falls back to the cached
  // lookup price when the card has never been priced (stale/0) or on error.
  const { data: suggested } = useListingSuggestedPrice(
    formData.card_id?.trim() || null,
    formData.finish,
    formData.condition
  );
  const suggestedPrice =
    suggested && !suggested.stale && suggested.price > 0 ? suggested.price : null;
  const marketPrice = suggestedPrice ?? lookUpData?.latest_price ?? null;
  // NOTE: the buyer-price computation below deliberately takes `suggestedPrice`
  // and NOT `marketPrice`.
  //
  // `suggested` answers the exact (finish, condition) basis, which is the same
  // row the backend requires when it saves a tracked listing (issue #108).
  // `latest_price` is the card's *representative* price — the cross-condition
  // guess the backend now refuses. Leaning on it quoted the seller a confident
  // buyer price for, say, a Damaged card off its Near Mint row, and the save
  // then 400'd. The readouts below already have the honest empty state ("No
  // market price, so use a fixed price."); it was simply unreachable while the
  // preview fell back.
  //
  // `marketPrice` keeps the fallback because it drives the "Market price"
  // readout and the bought-for default — "roughly what is this worth"
  // questions, not money the buyer pays.

  useEffect(() => {
    const parsed = parseFloat(marketPrice);
    if (!isNaN(parsed) && parsed > 0) {
      const clean = parsed.toFixed(2);
      setBasePrice(clean);
      updateFormData('basePrice', clean);
      updateFormData('tcgMarketPrice', parsed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketPrice]);

  const [followTCGPricing] = useState(formData.followTCGPricing || false);
  const [basePrice, setBasePrice] = useState(
    formData.basePrice || lookUpData?.latest_price || '0.00'
  );

  // Pricing mode: 'fixed' (seller sets an explicit price) vs 'tracked'
  // (price derives from the live market price + markup/floor). The backend
  // (Task 4) is authoritative on save and recomputes for tracked listings;
  // this is a live preview only.
  const [pricingMode, setPricingMode] = useState(formData.pricing_mode || 'fixed');
  const [feeFrame, setFeeFrame] = useState(formData.fee_frame || 'inclusive');
  // Prefilled with a real 0, not a placeholder that looks like one: the
  // backend needs an explicit markup for tracked listings.
  const [markupPercent, setMarkupPercent] = useState(
    formData.markup_percent != null && formData.markup_percent !== ''
      ? String(formData.markup_percent)
      : '0'
  );
  const [priceFloor, setPriceFloor] = useState(
    formData.price_floor != null ? String(formData.price_floor) : ''
  );
  const [fixedValue, setFixedValue] = useState(
    formData.pricing_mode !== 'tracked' && formData.price ? String(formData.price) : ''
  );

  const [quantity, setQuantity] = useState(formData.quantity || 1);
  const getInitialBoughtForPrice = () => {
    if (formData.bought_for_price) {
      return formData.bought_for_price;
    }
    const marketValue = formData.basePrice || lookUpData?.latest_price || '0.00';
    return marketValue !== '0.00' ? marketValue : '';
  };
  const [boughtForPrice, setBoughtForPrice] = useState(getInitialBoughtForPrice());

  useEffect(() => {
    if (followTCGPricing && lookUpData?.marketPrice?.[0]?.price) {
      const tcgPrice = lookUpData.marketPrice[0].price;
      setBasePrice(tcgPrice);
      updateFormData('basePrice', tcgPrice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookUpData?.marketPrice, followTCGPricing]);

  useEffect(() => {
    if (!formData.bought_for_price) {
      const marketValue = formData.basePrice || lookUpData?.latest_price;
      if (marketValue && marketValue !== '0.00' && parseFloat(marketValue) > 0) {
        const marketValueStr = formatNumber(parseFloat(marketValue), 2);
        if (boughtForPrice === '' || boughtForPrice === '0.00') {
          setBoughtForPrice(marketValueStr);
          updateFormData('bought_for_price', marketValueStr);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookUpData?.latest_price, formData.basePrice]);

  // Live buyer-price preview. Source of truth is computeBuyerPrice (Task 9)
  // and never derived locally as `x * (1 + fee)`. The backend recomputes
  // this authoritatively on save (tracked mode recomputes from the live
  // market price; fixed mode uses the submitted price as-is).
  const buyer = computeBuyerPrice({
    mode: pricingMode,
    frame: feeFrame,
    markupPercent,
    priceFloor,
    fixedValue,
    marketPrice: suggestedPrice,
    feeRate,
  });
  const bd = priceBreakdown(buyer ?? 0, feeRate);

  useEffect(() => {
    updateFormData('pricing_mode', pricingMode);
    updateFormData('fee_frame', feeFrame);
    updateFormData('markup_percent', pricingMode === 'tracked' ? markupPercent : '');
    updateFormData('price_floor', pricingMode === 'tracked' ? priceFloor : '');
    // FIXED mode: submit the RAW seller-entered value, not the grossed-up
    // buyer price, because the backend does its own frame gross-up from this raw
    // value, so submitting `buyer` here would double-gross fixed+exclusive
    // listings (see apps/cards/pricing.py compute_buyer_price). `buyer` is
    // still used above for the live preview only.
    updateFormData(
      'price',
      pricingMode === 'fixed' ? String(fixedValue ?? '') : String(buyer ?? '0')
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingMode, feeFrame, markupPercent, priceFloor, fixedValue, suggestedPrice, buyer]);

  const handleQuantityChange = (e) => {
    const value = Number.parseInt(e.target.value);
    const validValue = Math.max(1, value);
    setQuantity(validValue);
    updateFormData('quantity', validValue);
  };

  const handleBoughtForPriceChange = (e) => {
    let value = e.target.value;

    // Allow empty string, numbers, and decimals
    if (value === '') {
      setBoughtForPrice('');
      updateFormData('bought_for_price', '');
      return;
    }

    // Replace comma with dot for decimal input
    value = value.replace(/,/g, '.');

    // Validate: only numbers and one decimal point
    if (!/^\d*\.?\d*$/.test(value)) {
      return;
    }

    setBoughtForPrice(value);
    updateFormData('bought_for_price', value);
  };

  const handleMarkupChange = (e) => {
    let value = e.target.value.replace(/,/g, '.');
    if (value !== '' && !/^-?\d*\.?\d*$/.test(value)) {
      return;
    }
    setMarkupPercent(value);
  };

  const handleFloorChange = (e) => {
    let value = e.target.value.replace(/,/g, '.');
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) {
      return;
    }
    setPriceFloor(value);
  };

  const handleFixedValueChange = (e) => {
    let value = e.target.value.replace(/,/g, '.');
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) {
      return;
    }
    setFixedValue(value);
  };

  return (
    <PricingQuantityStepView
      titleRef={titleRef}
      errors={errors}
      basePrice={basePrice}
      pricingMode={pricingMode}
      onPricingModeChange={setPricingMode}
      feeFrame={feeFrame}
      onFeeFrameChange={setFeeFrame}
      markupPercent={markupPercent}
      onMarkupChange={handleMarkupChange}
      priceFloor={priceFloor}
      onFloorChange={handleFloorChange}
      fixedValue={fixedValue}
      onFixedValueChange={handleFixedValueChange}
      platformFee={platformFee}
      buyer={buyer}
      breakdown={bd}
      quantity={quantity}
      onQuantityChange={handleQuantityChange}
      boughtForPrice={boughtForPrice}
      onBoughtForPriceChange={handleBoughtForPriceChange}
    />
  );
};

export default PricingQuantityStep;
